import {
  BasicModal,
  Card,
  SelectLocationModal,
  StatusBar,
} from "src/components";
import { useDispatch, useSelector } from "react-redux";
import { State } from "src/state/state";
import styles from "src/pages/Menu/styles.module.scss";
import { getCartPath, getDealPath, getItemPath } from "src/Router/routes";
import { captureManualSentryException } from "src/common/sentry";
import { selectCategoriesWithAtLeastOneDisplayedItem } from "src/state/category/utils";
import { LOYALTY_TYPE } from "src/common/types/Loyalty";
import { faBagShopping, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  logPointsTooltipClickedToAnalytics,
  logUpdateOrderTypeToAnalytics,
  logViewCartClickedToAnalytics,
  logViewItemClickedToAnalytics,
} from "src/common/analytics";
import { selectDisplayedItemsFromState } from "src/state/item/utils";
import { selectDealsWithDisplayedItems } from "src/state/deal/utils";
import { CategoriesSlider } from "src/pages/Menu/CategoriesSlider/CategoriesSlider";
import classNames from "classnames";
import { ORDER_TYPE } from "src/state/order/types";
import { updateOrderTypeAction } from "src/state/orderType/actions";
import { isNowWithinDealScheduleHours } from "src/common/date";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { getAddressStringFromLocationEntity } from "src/common/address";
import { useScrollToTop } from "src/common/hooks";

export const Menu = () => {
  useScrollToTop();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cartDivRef = useRef<HTMLDivElement>(null);

  const restaurant = useSelector(
    (state: State) => state.restaurants.currentRestaurant,
  );
  const selectedLocation = useSelector(
    (state: State) => state.location.selectedLocation,
  );
  const selectedOrderType = useSelector(
    (state: State) => state.orderType.selectedOrderType,
  );
  const customer = useSelector(
    (state: State) => state.customers.currentCustomer,
  );

  const items = useSelector(selectDisplayedItemsFromState);
  const displayedCategories = useSelector(
    (state: State) =>
      restaurant && selectCategoriesWithAtLeastOneDisplayedItem(state, items),
  );
  const deals = useSelector((state: State) => {
    if (restaurant && displayedCategories) {
      const allDeals = Object.values(
        selectDealsWithDisplayedItems(state, displayedCategories, items)[
          restaurant.id
        ],
      );

      const dealsCurrentlyInSchedule = allDeals.filter((eachDeal) =>
        isNowWithinDealScheduleHours(eachDeal.dealSchedule),
      );

      return dealsCurrentlyInSchedule;
    }
  });
  const randomDeliveryTime = Math.floor(Math.random() * (35 - 20 + 1)) + 20;
  const estDeliveryTime =
    (restaurant?.restaurantSettings.minPreparationTime ?? 0) +
    randomDeliveryTime;

  const cart = useSelector((state: State) => state.cart);

  const cartAsArray = useMemo(() => Object.values(cart), [cart]);
  const cartSize = useMemo(() => {
    return cartAsArray.length;
  }, [cartAsArray]);
  const cartSubTotal = useMemo(
    () => cartAsArray && cartAsArray.reduce((a, v) => a + v.totalPrice, 0),
    [cartAsArray],
  );

  const categoriesArray = useMemo(
    () =>
      restaurant &&
      displayedCategories &&
      Object.values(displayedCategories[restaurant.id]).sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [restaurant, displayedCategories],
  );

  const [isPointsModalVisible, setIsPointsModalVisible] = useState(false);
  const [isLocationSelectorVisible, setIsLocationSelectorVisible] =
    useState(false);
  const [isFloatingButtonVisible, setIsFloatingButtonVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const goToCart = useCallback(() => {
    navigate(getCartPath());

    logViewCartClickedToAnalytics(customer?.id, cartSize, cartSubTotal);
  }, [navigate, customer, cartSize, cartSubTotal]);

  const isMobile = useMediaQuery({
    query: "(max-width: 700px)",
  });

  useEffect(() => {
    const handleScroll = () => {
      if (cartDivRef.current) {
        const targetPosition = cartDivRef.current.getBoundingClientRect();

        const isOffscreen = targetPosition.bottom < 0;

        if (
          isOffscreen &&
          restaurant &&
          restaurant.restaurantSettings.isOnlineOrderingEnabled
        ) {
          setIsFloatingButtonVisible(true);
        } else {
          setIsFloatingButtonVisible(false);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (!restaurant || !categoriesArray || !deals) {
    captureManualSentryException(
      new Error("restaurant or categoriesArray or deals is undefined in Menu"),
    );
    return <div />;
  }

  return (
    <div className={styles.overflowContainer}>
      <div
        className={styles.Menu}
        data-testid="menu-container"
        id="menu-container"
        style={{
          scrollMarginTop: isMobile ? "35px" : "0px",
        }}
      >
        {restaurant.isOwnerManaged && (
          <div className={styles.menuHeader} ref={cartDivRef}>
            {customer ? (
              <h1 className={styles.welcomeCustomer} data-testid="welcome-name">
                Welcome, {customer.firstName}!<br />
                Hungry?
              </h1>
            ) : (
              <div />
            )}
          </div>
        )}
        {customer &&
          restaurant.isOwnerManaged &&
          restaurant.loyalty.type !== LOYALTY_TYPE.NONE &&
          restaurant.restaurantSettings.isOnlineOrderingEnabled && (
            <div className={styles.customerPointsContainer}>
              <div
                className={styles.customerPointsTextRow}
                onClick={() => {
                  logPointsTooltipClickedToAnalytics(customer.id);
                  setIsPointsModalVisible(true);
                }}
              >
                <p
                  className={styles.customerPointsText}
                  data-testid="customer-points-text"
                >
                  {customer.points} available points
                </p>
                <FontAwesomeIcon
                  className={styles.circleInfoIcon}
                  icon={faCircleInfo}
                />
              </div>
              <StatusBar
                className={styles.pointsBar}
                indicatorPosition={customer.points}
              />
            </div>
          )}
        {selectedLocation &&
          restaurant.locations.length > 1 &&
          restaurant.isOwnerManaged && (
            <div
              className={styles.selectedLocationContainer}
              data-testid="location-container"
            >
              <h5
                className={styles.selectedLocationText}
                data-testid="location-text"
              >
                {getAddressStringFromLocationEntity(selectedLocation)}
              </h5>
              <h5
                data-testid="edit-location"
                className={styles.editLocationText}
                onClick={() => setIsLocationSelectorVisible(true)}
              >
                Edit
              </h5>
            </div>
          )}
        {restaurant.restaurantSettings.isOnlineOrderingEnabled === false &&
        restaurant.isOwnerManaged ? (
          <h1 className={styles.titleHeader} data-testid="order-now-text">
            Menu
          </h1>
        ) : restaurant.restaurantSettings.deliverySettings.enabled &&
          restaurant.isOwnerManaged ? (
          <div className={styles.toggleAndDeliveryContainer}>
            <div className={styles.toggleContainer}>
              <h4
                className={classNames(styles.leftToggleInternal, {
                  [styles.leftToggleActive]:
                    selectedOrderType === ORDER_TYPE.PICKUP,
                })}
                onClick={() => {
                  updateOrderTypeAction(ORDER_TYPE.PICKUP)(dispatch);
                  logUpdateOrderTypeToAnalytics(
                    customer?.id,
                    ORDER_TYPE.PICKUP,
                    "MENU",
                  );
                }}
                data-testid="pickup-toggle"
              >
                Pickup
              </h4>
              <h4
                className={classNames(styles.rightToggleInternal, {
                  [styles.rightToggleActive]:
                    selectedOrderType === ORDER_TYPE.DELIVERY,
                })}
                onClick={() => {
                  updateOrderTypeAction(ORDER_TYPE.DELIVERY)(dispatch);
                  logUpdateOrderTypeToAnalytics(
                    customer?.id,
                    ORDER_TYPE.DELIVERY,
                    "MENU",
                  );
                }}
                data-testid="delivery-toggle"
              >
                Delivery
              </h4>
            </div>
            {selectedOrderType === ORDER_TYPE.DELIVERY && (
              <span className={styles.deliveryTime} data-testid="delivery-time">
                {`Est. ${estDeliveryTime} min`}
              </span>
            )}
          </div>
        ) : restaurant.isOwnerManaged ? (
          <h1 className={styles.titleHeader} data-testid="order-now-text">
            Order Now
          </h1>
        ) : (
          <div />
        )}
        <CategoriesSlider
          deals={deals}
          categoriesArray={categoriesArray}
          onSearchValueChange={setSearchValue}
        />
        {deals.length > 0 && (
          <div
            className={styles.dealsContainer}
            id={"deals-container"}
            data-testid="deals-container"
          >
            <h2 className={styles.sectionHeader}>Deals</h2>
            <div className={styles.itemsGrid}>
              {deals.map((deal) => (
                <Card
                  testId={`deal-card-${deal.id}`}
                  link={getDealPath(deal.id)}
                  key={deal.id}
                  image={deal.imageUrl}
                  title={deal.name}
                  description={
                    deal.description.length > 0 ? deal.description : undefined
                  }
                />
              ))}
            </div>
          </div>
        )}
        <div className={styles.categoriesContainer}>
          {categoriesArray.map((category) => {
            const filteredItemsInThisCategory = Object.values(
              items[category.id],
            )
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .filter((item) =>
                item.name.toLowerCase().includes(searchValue.toLowerCase()),
              );

            if (filteredItemsInThisCategory.length === 0) {
              return null;
            }

            return (
              <div
                className={styles.categoryContainer}
                data-testid={`category-container-${category.id}`}
                key={category.id}
                id={category.id}
              >
                <h2 className={styles.sectionHeader}>{category.name}</h2>
                <div className={styles.itemsGrid}>
                  {filteredItemsInThisCategory.map((item) => (
                    <Card
                      testId={`item-card-${item.id}`}
                      link={getItemPath(category.id, item.id)}
                      key={item.id}
                      image={item.hasImage ? item.imageURL : undefined}
                      title={item.name}
                      description={item.description}
                      price={item.price}
                      onClick={() => {
                        logViewItemClickedToAnalytics(
                          customer?.id,
                          item.id,
                          item.name,
                        );
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          {isFloatingButtonVisible &&
            (cartSize > 0 ? (
              <div className={styles.floatingCheckoutButton} onClick={goToCart}>
                <p
                  className={styles.floatingCheckoutText}
                >{`Checkout (${cartSize})`}</p>
              </div>
            ) : (
              <div
                data-testid={"menu-cart-icon"}
                className={styles.floatingCartButton}
                onClick={goToCart}
              >
                <FontAwesomeIcon
                  className={styles.floatingCart}
                  icon={faBagShopping}
                />
              </div>
            ))}
        </div>
        <BasicModal
          height="275"
          testId="points-modal"
          isModalVisible={isPointsModalVisible}
          title={"Points"}
          message={`Points are earned by placing orders from the menu. For every $1 you spend, you get 100 points. Every ${
            (restaurant.loyalty.pointsSystemAmountSpend as number) * 100
          } points you earn, you get $${
            restaurant.loyalty.pointsSystemAmountEarn
          } off your next order. Eat up!`}
          onConfirm={() => setIsPointsModalVisible(false)}
          onClickOutside={() => setIsPointsModalVisible(false)}
        />
        <SelectLocationModal
          testId="menu-select-location-modal"
          isOpen={isLocationSelectorVisible}
          onClose={() => setIsLocationSelectorVisible(false)}
        />
      </div>
    </div>
  );
};
